import React from 'react'
import { useIntl } from 'react-intl'
import { ConfirmationRoleAction } from '../../../legacy-design-system/action'
import { RenderDialogProps } from '../../../legacy-design-system/action/confirmation-action'
import { Role, RoleSwitch } from '../../roles'
import { PureQueryOptions } from 'apollo-client'
import { MergeVulnerabilitiesDialog } from './merge-dialog'
import { DateTime } from '../shared'
import { IconMerge } from '@contrast-security-inc/design-system-foundations'
import { ActionButton } from '@contrast-security-inc/design-system-actions'
import { ConfirmationRoleActionProps } from '../../../legacy-design-system/action/confirmation-role-action'
export interface Vulnerability {
  readonly id: string
  readonly title: string
  readonly ruleName: string
  readonly lastDetected: DateTime
  readonly application: {
    readonly id: string
    readonly licensed: boolean
    readonly role: Role
  }
}

export interface MutationProps {
  readonly refetchQueries: PureQueryOptions[]
  readonly refresh: () => Promise<any>
}

interface BulkActionProps extends Pick<ConfirmationRoleActionProps, 'size'> {
  readonly organizationRole: Role
  readonly vulnerabilities: Vulnerability[]
  readonly organizationID: string
  readonly refetchQueries: PureQueryOptions[]
  readonly refresh: () => Promise<any>
}

const requiredRoles = new Set(['EDIT', 'RULES_ADMIN', 'ADMIN'])

export function isActive(vulnerabilities: Vulnerability[]) {
  return (
    // multiple vulns
    vulnerabilities.length >= 2 &&
    // vulns belong to same app
    new Set(vulnerabilities.map((v) => v.application.id)).size === 1 &&
    // vulns are of same type
    new Set(vulnerabilities.map((v) => v.ruleName)).size === 1 &&
    // vuln app is licensed
    vulnerabilities[0].application.licensed === true &&
    // vuln app role is correct
    requiredRoles.has(vulnerabilities[0].application.role)
  )
}

export function MergeVulnerabilitiesAction({
  vulnerabilities,
  organizationID,
  refetchQueries,
  refresh,
  organizationRole,
  size
}: BulkActionProps) {
  const { formatMessage } = useIntl()
  return isActive(vulnerabilities) ? (
    <ConfirmationRoleAction
      size={size}
      data-testid="action-merge"
      isActionValid
      requiredRole="EDIT"
      icon={IconMerge}
      iconTooltipMessage={{ id: 'action_merge' }}
      role={vulnerabilities[0].application.role}
      iconTooltipPlacement="top"
      renderDialog={({ isOpen, closeDialog }: RenderDialogProps) => (
        <MergeVulnerabilitiesDialog
          organizationID={organizationID}
          vulnerabilities={vulnerabilities}
          closeDialog={closeDialog}
          isOpen={isOpen}
          refetchQueries={refetchQueries}
          refresh={refresh}
        />
      )}
    />
  ) : (
    <RoleSwitch
      role={organizationRole}
      requiredRole="EDIT"
      renderAllowed={() => {
        return (
          <ActionButton
            isDisabled
            data-testid="action-merge"
            IconButtonProps={{
              Icon: IconMerge,
              'aria-label': formatMessage({ id: 'action_merge' }),
              size
            }}
            TooltipProps={{
              body: formatMessage(
                {
                  id: 'title_vulnerabilities_mergeRequirements'
                },
                { strong: (msg) => <strong>{msg}</strong> }
              )
            }}
          ></ActionButton>
        )
      }}
      renderDenied={undefined}
    />
  )
}
